import { CommonModule } from "@angular/common";
import { Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { catchError, of } from "rxjs";

import { HearingHistoryEntry } from "@app/case-context/models/hearing-history.model";
import { HearingHistoryService } from "@app/case-context/services/hearing-history.service";
import { PHMS_CASE_CONTEXT_TERMINOLOGY } from "@app/phms/phms-case-context.terminology";
import { formatRecordLabel } from "@app/case-context/components/case-context-record-grid/case-context-record-grid-columns";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";

/**
 * Hearing-history table for the detail page (PHMS-IP-9): prior board hearings (date, type, decision,
 * panel, notes) from the hearing-history read model, most-recent first. Loading / empty / error
 * states never hide the section.
 */
@Component({
  selector: "phms-case-context-hearing-history-panel",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, TerminologyIdentityDirective],
  templateUrl: "./case-context-hearing-history-panel.component.html",
  styleUrl: "./case-context-hearing-history-panel.component.scss",
})
export class CaseContextHearingHistoryPanelComponent implements OnInit, OnChanges {
  @Input({ required: true }) incarceratedPersonId!: number;

  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(HearingHistoryService);

  readonly sectionUsage = PHMS_CASE_CONTEXT_TERMINOLOGY.hearingHistory;
  readonly sectionText = createTerminologyUsageSignals(this.sectionUsage);

  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly entries = signal<HearingHistoryEntry[]>([]);

  readonly isEmpty = computed(() => !this.hasError() && this.entries().length === 0);

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["incarceratedPersonId"] && !changes["incarceratedPersonId"].firstChange) {
      this.load();
    }
  }

  load(): void {
    if (!this.incarceratedPersonId || this.incarceratedPersonId <= 0) {
      this.entries.set([]);
      return;
    }

    this.isLoading.set(true);
    this.hasError.set(false);
    this.entries.set([]);

    this.service
      .getHearingHistory(this.incarceratedPersonId)
      .pipe(
        catchError(() => {
          this.hasError.set(true);
          return of([] as HearingHistoryEntry[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.entries.set(result ?? []);
        this.isLoading.set(false);
      });
  }

  /** Maps a decision code to a tone class for the decision pill. */
  decisionTone(code: string | null | undefined): "granted" | "denied" | "continued" | "default" {
    switch ((code ?? "").toLowerCase()) {
      case "granted":
        return "granted";
      case "denied":
        return "denied";
      case "continued":
        return "continued";
      default:
        return "default";
    }
  }

  decisionLabel(code: string | null | undefined): string {
    return formatRecordLabel(code);
  }
}
